import { siteContent } from '@/mocks/siteContent';

export default function ContentSections() {
  const { whatIs, whySection, howItWorks, reviewSection, shippingGuide, couponSection, warehouseGuide, safetySection } = siteContent;

  return (
    <div id="guides">
      {/* What Is Section */}
      <SectionWrapper bg="bg-background-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-6 text-center">
            {whatIs.title}
          </h2>
          <div className="space-y-4">
            {whatIs.paragraphs.map((p, i) => (
              <p key={i} className="text-sm md:text-base text-foreground-600 leading-relaxed">{p}</p>
            ))}
          </div>
        </div>
      </SectionWrapper>

      {/* Why Section */}
      <SectionWrapper bg="bg-background-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-8 text-center">
            {whySection.title}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-background-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-800 whitespace-nowrap">Aspect</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-500 whitespace-nowrap">Traditional Search</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-primary-600 whitespace-nowrap">Sugargoo Spreadsheet</th>
                </tr>
              </thead>
              <tbody>
                {whySection.rows.map((row, i) => (
                  <tr key={i} className="border-b border-background-100">
                    <td className="py-3.5 px-4 text-sm font-medium text-foreground-800 whitespace-nowrap">{row.label}</td>
                    <td className="py-3.5 px-4 text-sm text-foreground-500">{row.traditional}</td>
                    <td className="py-3.5 px-4 text-sm text-primary-600 font-medium">{row.spreadsheet}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </SectionWrapper>

      {/* Category Dropdown Section (standalone, also linked from cards) */}
      <div id="category-dropdown-section" className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4">
            Browse All Sugargoo Spreadsheet Categories
          </h2>
          <p className="text-sm md:text-base text-foreground-600 mb-10">
            Select a category below to explore products on our main website
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {[
              { name: 'Shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html', img: 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20white%20sneakers%20on%20a%20warm%20cream%20surface%20with%20soft%20natural%20lighting%2C%20modern%20boutique%20aesthetic%2C%20warm%20neutral%20tones%2C%20product%20photography%20with%20soft%20shadows%20and%20simple%20elegant%20composition&width=400&height=300&seq=cat-nav-shoes-v2&orientation=landscape' },
              { name: 'Hoodies', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=A%20soft%20cozy%20oatmeal%20colored%20hoodie%20folded%20on%20a%20light%20wooden%20surface%2C%20warm%20ambient%20light%2C%20minimalist%20clothing%20aesthetic%2C%20cream%20and%20beige%20palette%2C%20clean%20product%20photography%20with%20gentle%20shadows&width=400&height=300&seq=cat-nav-hoodies-v2&orientation=landscape' },
              { name: 'T-Shirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=Premium%20cotton%20t-shirts%20in%20earthy%20tones%20stacked%20neatly%20on%20a%20cream%20textile%2C%20soft%20daylight%2C%20warm%20neutral%20minimal%20aesthetic%2C%20clean%20product%20photography%20with%20fabric%20texture%20visible&width=400&height=300&seq=cat-nav-tshirts-v2&orientation=landscape' },
              { name: 'Jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=A%20tailored%20warm%20beige%20jacket%20on%20a%20wooden%20hanger%20against%20cream%20wall%2C%20soft%20golden%20light%2C%20sophisticated%20warm%20palette%2C%20clean%20fashion%20photography%20with%20elegant%20simple%20composition&width=400&height=300&seq=cat-nav-jackets-v2&orientation=landscape' },
              { name: 'Pants', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=Well%20tailored%20linen%20trousers%20folded%20on%20a%20warm%20sand%20surface%2C%20soft%20natural%20light%2C%20Mediterranean%20aesthetic%2C%20cream%20tones%2C%20clean%20product%20photography%20with%20texture%20detail&width=400&height=300&seq=cat-nav-pants-v2&orientation=landscape' },
              { name: 'Headwear', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=A%20stylish%20hat%20resting%20on%20a%20vintage%20wooden%20table%2C%20warm%20filtered%20light%2C%20cream%20backdrop%2C%20sophisticated%20accessory%20photography%20with%20gentle%20shadows&width=400&height=300&seq=cat-nav-headwear-v2&orientation=landscape' },
              { name: 'Sets', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=A%20coordinated%20fashion%20outfit%20ensemble%20laid%20on%20a%20light%20oak%20floor%2C%20warm%20daylight%2C%20minimalist%20interior%2C%20refined%20neutral%20palette%2C%20editorial%20fashion%20photography&width=400&height=300&seq=cat-nav-sets-v2&orientation=landscape' },
              { name: 'Underwear', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=Premium%20cotton%20undergarments%20on%20a%20soft%20cream%20textile%2C%20gentle%20warm%20light%2C%20clean%20minimal%20aesthetic%2C%20ivory%20tones%2C%20refined%20product%20photography&width=400&height=300&seq=cat-nav-underwear-v2&orientation=landscape' },
              { name: 'Jersey', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=A%20vintage%20athletic%20jersey%20on%20a%20warm%20wooden%20surface%2C%20soft%20sunlight%2C%20warm%20cream%20and%20muted%20sport%20tones%2C%20nostalgic%20editorial%20sports%20photography&width=400&height=300&seq=cat-nav-jersey-v2&orientation=landscape' },
              { name: 'Accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=Elegant%20minimalist%20jewelry%20on%20a%20warm%20marble%20surface%2C%20soft%20ambient%20light%2C%20gold%20and%20cream%20tones%2C%20refined%20luxury%20accessory%20photography&width=400&height=300&seq=cat-nav-accessories-v2&orientation=landscape' },
              { name: 'Bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=A%20structured%20leather%20tote%20bag%20on%20a%20light%20oak%20bench%2C%20warm%20natural%20light%2C%20cream%20wall%20background%2C%20earthy%20tones%2C%20elegant%20boutique%20product%20photography&width=400&height=300&seq=cat-nav-bags-v2&orientation=landscape' },
              { name: 'Belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=A%20handcrafted%20leather%20belt%20coiled%20on%20a%20warm%20wooden%20surface%2C%20golden%20hour%20light%2C%20cognac%20tones%2C%20artisanal%20accessory%20photography%20with%20organic%20texture&width=400&height=300&seq=cat-nav-belts-v2&orientation=landscape' },
              { name: 'Perfumes', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=An%20elegant%20amber%20perfume%20bottle%20on%20a%20cream%20marble%20surface%2C%20soft%20diffused%20light%2C%20golden%20and%20cream%20tones%2C%20luxury%20fragrance%20photography%20with%20refined%20aesthetic&width=400&height=300&seq=cat-nav-perfumes-v2&orientation=landscape' },
              { name: 'Other Stuff', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html', img: 'https://readdy.ai/api/search-image?query=A%20curated%20collection%20of%20lifestyle%20accessories%20on%20a%20warm%20sand%20surface%2C%20soft%20daylight%2C%20minimalist%20aesthetic%20with%20earthy%20tones%2C%20refined%20product%20photography&width=400&height=300&seq=cat-nav-other-v2&orientation=landscape' },
            ].map((cat, i) => (
              <a
                key={i}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group flex flex-col bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-primary-300 transition-all duration-200 cursor-pointer"
              >
                <div className="w-full h-32 overflow-hidden">
                  <img src={cat.img} alt={cat.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <span className="px-3 py-2.5 text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors text-center whitespace-nowrap">
                  {cat.name}
                </span>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* How It Works */}
      <SectionWrapper bg="bg-background-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-10 text-center">
            {howItWorks.title}
          </h2>
          <div className="space-y-8">
            {howItWorks.steps.map((step, i) => (
              <div key={i} className="flex gap-5">
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 font-heading text-lg font-bold">
                  {step.number}
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{step.title}</h3>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SectionWrapper>

      {/* Review Section */}
      <SectionWrapper bg="bg-background-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 text-center">
            {reviewSection.title}
          </h2>
          <p className="text-sm md:text-base text-foreground-600 text-center mb-10 max-w-2xl mx-auto">{reviewSection.intro}</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
            <div className="bg-background-50 border border-accent-200/60 rounded-xl p-6">
              <h3 className="font-heading text-lg font-semibold text-accent-700 mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-thumb-up-line"></i>
                </div>
                Advantages
              </h3>
              <ul className="space-y-3">
                {reviewSection.pros.map((pro, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-accent-500 text-xs"></i>
                    </div>
                    {pro}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-background-50 border border-secondary-200/60 rounded-xl p-6">
              <h3 className="font-heading text-lg font-semibold text-secondary-700 mb-4 flex items-center gap-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-information-line"></i>
                </div>
                Considerations
              </h3>
              <ul className="space-y-3">
                {reviewSection.cons.map((con, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground-700">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-subtract-line text-secondary-500 text-xs"></i>
                    </div>
                    {con}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse bg-background-50 rounded-xl border border-background-200/60">
              <thead>
                <tr className="border-b border-background-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-800 whitespace-nowrap">Feature</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-primary-600 whitespace-nowrap">Sugargoo</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold text-foreground-500 whitespace-nowrap">Other Agents</th>
                </tr>
              </thead>
              <tbody>
                {reviewSection.comparison.map((row, i) => (
                  <tr key={i} className="border-b border-background-100 last:border-b-0">
                    <td className="py-3 px-4 text-sm font-medium text-foreground-800 whitespace-nowrap">{row.feature}</td>
                    <td className="py-3 px-4 text-sm text-primary-600">{row.sugargoo}</td>
                    <td className="py-3 px-4 text-sm text-foreground-500">{row.others}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </SectionWrapper>

      {/* Shipping Guide */}
      <SectionWrapper bg="bg-background-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 text-center">
            {shippingGuide.title}
          </h2>
          <p className="text-sm md:text-base text-foreground-600 text-center mb-10 max-w-2xl mx-auto">{shippingGuide.intro}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {shippingGuide.factors.map((factor, i) => (
              <div key={i} className="bg-background-100 border border-background-200/60 rounded-xl p-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/70 mb-3">
                  <i className={`${factor.icon} text-accent-600 text-lg`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{factor.name}</h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{factor.description}</p>
              </div>
            ))}
          </div>
        </div>
      </SectionWrapper>

      {/* Coupon Section */}
      <SectionWrapper bg="bg-background-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 text-center">
            {couponSection.title}
          </h2>
          <p className="text-sm md:text-base text-foreground-600 text-center mb-10 max-w-2xl mx-auto">{couponSection.intro}</p>
          <div className="bg-background-50 border border-background-200/60 rounded-xl p-6 md:p-8">
            <ul className="space-y-4">
              {couponSection.tips.map((tip, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary-600">{i + 1}</span>
                  </div>
                  <p className="text-sm md:text-base text-foreground-700">{tip}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </SectionWrapper>

      {/* Warehouse Guide */}
      <SectionWrapper bg="bg-background-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 text-center">
            {warehouseGuide.title}
          </h2>
          <p className="text-sm md:text-base text-foreground-600 text-center mb-10 max-w-2xl mx-auto">{warehouseGuide.intro}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {warehouseGuide.features.map((feat, i) => (
              <div key={i} className="bg-background-100 border border-background-200/60 rounded-xl p-5">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{feat.title}</h3>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{feat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </SectionWrapper>

      {/* Safety Section */}
      <SectionWrapper bg="bg-background-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 text-center">
            {safetySection.title}
          </h2>
          <p className="text-sm md:text-base text-foreground-600 text-center mb-10 max-w-2xl mx-auto">{safetySection.intro}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {safetySection.points.map((point, i) => (
              <div key={i} className="bg-background-50 border border-background-200/60 rounded-xl p-5 flex gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100/70 flex-shrink-0">
                  <i className="ri-shield-check-line text-accent-600 text-lg"></i>
                </div>
                <div>
                  <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">{point.title}</h3>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">{point.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SectionWrapper>
    </div>
  );
}

function SectionWrapper({ children, bg }: { children: React.ReactNode; bg: string }) {
  return (
    <section className={`py-16 md:py-20 ${bg}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {children}
      </div>
    </section>
  );
}